import React, { useState } from 'react';
import { importJobsAPI } from '../api/importJobs';

/**
 * `asImportJob` (new) — when true, the submission is wrapped in an ImportJob
 * record that persists across page loads. Returns { job, results } from the
 * import-jobs endpoint so the parent can show the job in a Imports panel.
 * When false (default = legacy behavior), submits directly via `apiEndpoint`
 * or the campaign-scoped endpoint.
 */
const BulkCreatorUpload = ({ isOpen, onClose, campaignId, onSuccess, apiEndpoint, asImportJob = false }) => {
  const [inputMethod, setInputMethod] = useState('paste'); // 'paste' | 'csv'
  const [pasteInput, setPasteInput] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [importName, setImportName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // Parse pasted input - URLs only, no plain usernames
  const parsePasteInput = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const creators = [];
    const skipped = [];

    for (const line of lines) {
      const trimmed = line.trim();

      // Only accept URLs, not plain usernames
      let creator = { raw: trimmed };
      let isValidUrl = false;

      // Twitch URL
      const twitchMatch = trimmed.match(/twitch\.tv\/([a-zA-Z0-9_]+)/i);
      if (twitchMatch) {
        creator.twitchHandle = twitchMatch[1];
        creator.platform = 'twitch';
        isValidUrl = true;
      }

      // Kick URL
      const kickMatch = trimmed.match(/kick\.com\/([a-zA-Z0-9_-]+)/i);
      if (kickMatch) {
        creator.kickHandle = kickMatch[1].toLowerCase();
        creator.kickUrl = `https://kick.com/${creator.kickHandle}`;
        creator.platform = 'kick';
        isValidUrl = true;
      }

      // YouTube URL
      const youtubeMatch = trimmed.match(/youtube\.com\/(channel\/|c\/|@|user\/)?([a-zA-Z0-9_-]+)/i);
      if (youtubeMatch) {
        creator.youtubeHandle = youtubeMatch[2];
        creator.platform = 'youtube';
        isValidUrl = true;
      }

      // TikTok URL
      const tiktokMatch = trimmed.match(/tiktok\.com\/@?([a-zA-Z0-9_.]+)/i);
      if (tiktokMatch) {
        creator.tiktokHandle = tiktokMatch[1];
        creator.platform = 'tiktok';
        isValidUrl = true;
      }

      // Twitter/X URL (for social link, not primary platform)
      const twitterMatch = trimmed.match(/(twitter|x)\.com\/([a-zA-Z0-9_]+)/i);
      if (twitterMatch) {
        creator.twitterHandle = twitterMatch[2];
      }

      // Instagram URL
      const instagramMatch = trimmed.match(/instagram\.com\/([a-zA-Z0-9_.]+)/i);
      if (instagramMatch) {
        creator.instagramHandle = instagramMatch[1];
      }

      // Only add if we found a valid platform URL (Twitch, YouTube, or TikTok)
      if (isValidUrl) {
        creators.push(creator);
      } else if (trimmed.length > 0) {
        // Skip plain usernames - they need to provide a URL
        skipped.push(trimmed);
      }
    }

    // Log skipped entries for debugging
    if (skipped.length > 0) {
      console.log('Skipped non-URL entries:', skipped);
    }

    return creators;
  };

  // RFC-4180-ish quote-aware CSV row tokenizer. Handles:
  //  - quoted fields containing commas ("1,234")
  //  - quoted fields containing newlines
  //  - escaped quotes inside a field ("a ""b"" c")
  // Returns an array of rows (each row = array of cell strings, no quotes).
  const parseCsvRows = (text) => {
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { cell += '"'; i++; }
          else { inQuotes = false; }
        } else {
          cell += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          row.push(cell); cell = '';
        } else if (ch === '\n') {
          row.push(cell); cell = '';
          rows.push(row); row = [];
        } else {
          cell += ch;
        }
      }
    }
    // flush tail
    if (cell.length > 0 || row.length > 0) { row.push(cell); rows.push(row); }
    return rows;
  };

  // Parse CSV file - extracts URLs and handles from various column formats.
  // Uses a quote-aware parser so that cells containing commas (e.g. numeric
  // "1,234" or multi-URL cells "url1, url2") don't shift subsequent columns.
  const parseCSV = (text) => {
    // Strip UTF-8 BOM and normalize line endings
    const clean = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
    const rows = parseCsvRows(clean);
    if (rows.length === 0) return [];
    const headers = rows[0].map(h => h.trim().toLowerCase());
    const creators = [];

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (row.length === 1 && !row[0].trim()) continue; // blank line
      const values = row.map(v => v.trim());

      const creator = {};
      headers.forEach((header, idx) => {
        const value = values[idx];
        if (!value) return;

        // Map common header names to standardized fields
        const mapping = {
          'name': 'name',
          'creator': 'name',
          'creator_name': 'name',
          // Twitch
          'twitch': 'twitchUrl',
          'twitch_handle': 'twitchUrl',
          'twitchhandle': 'twitchUrl',
          'twitch_url': 'twitchUrl',
          'twitchurl': 'twitchUrl',
          // Kick
          'kick': 'kickUrl',
          'kick_handle': 'kickUrl',
          'kickhandle': 'kickUrl',
          'kick_url': 'kickUrl',
          'kickurl': 'kickUrl',
          // YouTube
          'youtube': 'youtubeUrl',
          'youtube_handle': 'youtubeUrl',
          'youtubehandle': 'youtubeUrl',
          'youtube_url': 'youtubeUrl',
          'youtubeurl': 'youtubeUrl',
          // TikTok
          'tiktok': 'tiktokUrl',
          'tiktok_handle': 'tiktokUrl',
          'tiktokhandle': 'tiktokUrl',
          'tiktok_url': 'tiktokUrl',
          'tiktokurl': 'tiktokUrl',
          // Twitter/X
          'twitter': 'twitterUrl',
          'twitter_handle': 'twitterUrl',
          'twitterhandle': 'twitterUrl',
          'twitter_url': 'twitterUrl',
          'twitterurl': 'twitterUrl',
          'x': 'twitterUrl',
          'x_url': 'twitterUrl',
          // Instagram
          'instagram': 'instagramUrl',
          'instagram_handle': 'instagramUrl',
          'instagramhandle': 'instagramUrl',
          'instagram_url': 'instagramUrl',
          'instagramurl': 'instagramUrl',
          'ig': 'instagramUrl',
          // Country/Region
          'country': 'country',
          'region': 'country',
          'country_code': 'country',
          'countrycode': 'country',
          'location': 'country',
          // Other
          'email': 'email',
          'contact_email': 'email',
          'contactemail': 'email',
          'business_email': 'email',
          // Genres
          'genre': 'genres',
          'genres': 'genres',
          'niche': 'genres',
          'category': 'genres',
          // Twitch CCV (concurrent viewers). Export writes this, so round-
          // tripping a platform export through import preserves the value.
          'twitch ccv': 'twitchCcv',
          'twitch_ccv': 'twitchCcv',
          'twitchccv': 'twitchCcv',
          'avg ccv': 'twitchCcv',
          'avg_ccv': 'twitchCcv',
          'ccv': 'twitchCcv',
        };

        const field = mapping[header] || header;
        creator[field] = value;
      });

      // If a URL cell holds multiple comma-separated URLs (e.g. several
      // YouTube channels for the same creator), keep only the first one.
      // The backend links additional platforms later, and we don't want
      // "url1, url2" to break URL-matching regexes downstream.
      const firstUrl = (v) => (v || '').split(/[,\s]+/).map(s => s.trim()).filter(Boolean)[0] || '';
      if (creator.twitchUrl) creator.twitchUrl = firstUrl(creator.twitchUrl);
      if (creator.kickUrl) creator.kickUrl = firstUrl(creator.kickUrl);
      if (creator.youtubeUrl) creator.youtubeUrl = firstUrl(creator.youtubeUrl);
      if (creator.tiktokUrl) creator.tiktokUrl = firstUrl(creator.tiktokUrl);
      if (creator.twitterUrl) creator.twitterUrl = firstUrl(creator.twitterUrl);
      if (creator.instagramUrl) creator.instagramUrl = firstUrl(creator.instagramUrl);

      // CCV comes in as "12,500" or "1,234" — strip separators and parse
      // to int. Anything non-numeric (e.g. "N/A") becomes undefined so the
      // backend backfill stays skipped.
      if (creator.twitchCcv) {
        const n = parseInt(String(creator.twitchCcv).replace(/[^\d]/g, ''), 10);
        if (Number.isFinite(n) && n > 0) creator.twitchCcv = n;
        else delete creator.twitchCcv;
      }

      // Normalize URLs - convert handles to full URLs if needed
      if (creator.twitchUrl) {
        if (!creator.twitchUrl.includes('twitch.tv')) {
          // It's just a handle, convert to URL
          creator.twitchUrl = `https://www.twitch.tv/${creator.twitchUrl.replace('@', '')}`;
        }
        const match = creator.twitchUrl.match(/twitch\.tv\/([a-zA-Z0-9_]+)/i);
        if (match) {
          creator.twitchHandle = match[1];
          creator.platform = creator.platform || 'twitch';
        }
      }

      if (creator.kickUrl) {
        if (!creator.kickUrl.includes('kick.com')) {
          // It's just a handle, convert to URL
          creator.kickUrl = `https://kick.com/${creator.kickUrl.replace('@', '').toLowerCase()}`;
        }
        const match = creator.kickUrl.match(/kick\.com\/([a-zA-Z0-9_-]+)/i);
        if (match) {
          creator.kickHandle = match[1].toLowerCase();
          creator.platform = creator.platform || 'kick';
        }
      }

      if (creator.youtubeUrl) {
        if (!creator.youtubeUrl.includes('youtube.com')) {
          // It's just a handle, convert to URL
          const handle = creator.youtubeUrl.replace('@', '');
          creator.youtubeUrl = `https://www.youtube.com/@${handle}`;
        }
        const match = creator.youtubeUrl.match(/youtube\.com\/(channel\/|c\/|@|user\/)?([a-zA-Z0-9_-]+)/i);
        if (match) {
          creator.youtubeHandle = match[2];
          creator.platform = creator.platform || 'youtube';
        }
      }

      if (creator.tiktokUrl) {
        if (!creator.tiktokUrl.includes('tiktok.com')) {
          // It's just a handle, convert to URL
          const handle = creator.tiktokUrl.replace('@', '');
          creator.tiktokUrl = `https://www.tiktok.com/@${handle}`;
        }
        const match = creator.tiktokUrl.match(/tiktok\.com\/@?([a-zA-Z0-9_.]+)/i);
        if (match) {
          creator.tiktokHandle = match[1];
          creator.platform = creator.platform || 'tiktok';
        }
      }

      if (creator.twitterUrl) {
        if (!creator.twitterUrl.includes('twitter.com') && !creator.twitterUrl.includes('x.com')) {
          // It's just a handle, convert to URL
          creator.twitterUrl = `https://twitter.com/${creator.twitterUrl.replace('@', '')}`;
        }
        const match = creator.twitterUrl.match(/(?:twitter|x)\.com\/([a-zA-Z0-9_]+)/i);
        if (match) {
          creator.twitterHandle = match[1];
        }
      }

      if (creator.instagramUrl) {
        if (!creator.instagramUrl.includes('instagram.com')) {
          // It's just a handle, convert to URL
          creator.instagramUrl = `https://www.instagram.com/${creator.instagramUrl.replace('@', '')}`;
        }
        const match = creator.instagramUrl.match(/instagram\.com\/([a-zA-Z0-9_.]+)/i);
        if (match) {
          creator.instagramHandle = match[1];
        }
      }

      // Only add if we have at least one platform URL (Twitch, Kick, YouTube, or TikTok)
      if (creator.twitchHandle || creator.kickHandle || creator.youtubeHandle || creator.tiktokHandle) {
        creators.push(creator);
      }
    }

    return creators;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setError('');
    }
  };

  const handleProcess = async () => {
    setProcessing(true);
    setError('');
    setResults(null);

    try {
      let parsedCreators = [];

      if (inputMethod === 'paste') {
        if (!pasteInput.trim()) {
          throw new Error('Please enter some creator handles or URLs');
        }
        parsedCreators = parsePasteInput(pasteInput);
      } else {
        if (!csvFile) {
          throw new Error('Please select a CSV file');
        }
        const text = await csvFile.text();
        parsedCreators = parseCSV(text);
      }

      if (parsedCreators.length === 0) {
        throw new Error('No valid creator URLs found. Please enter Twitch, Kick, YouTube, or TikTok URLs.');
      }

      console.log('Parsed creators:', parsedCreators);

      // Send to backend for processing
      let data;
      if (asImportJob) {
        // Auto-name from CSV filename if the user didn't type a name
        const autoName = !importName.trim() && csvFile
          ? csvFile.name.replace(/\.csv$/i, '').replace(/[_-]/g, ' ')
          : null;

        const response = await importJobsAPI.create({
          name: importName.trim() || autoName || undefined,
          inputSource: inputMethod,
          creators: parsedCreators,
        });
        const jobData = response.data;
        data = {
          job: jobData.job,
          processing: jobData.processing,
          added: jobData.job?.totalRequested || parsedCreators.length,
          message: `Import "${jobData.job?.name}" started. ${parsedCreators.length} creators are being processed in the background. Check the Imports panel for progress.`,
        };
      } else if (apiEndpoint) {
        // Use the provided API function (for general creators page — legacy)
        const response = await apiEndpoint(parsedCreators);
        data = response.data;
      } else {
        // Use the campaign-specific endpoint (backwards compatibility)
        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/creators/bulk`;
        console.log('Calling API:', apiUrl);
        console.log('Campaign ID:', campaignId);
        console.log('Token:', localStorage.getItem('token') ? 'present' : 'missing');

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ creators: parsedCreators })
        });

        console.log('Response status:', response.status);

        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          console.error('Non-JSON response from server:', text);
          throw new Error(`Server error: ${response.status} - ${text.substring(0, 200)}`);
        }

        if (!response.ok) {
          throw new Error(data.error || `Server error: ${response.status}`);
        }
      }

      setResults(data);

    } catch (err) {
      console.error('Error in bulk upload:', err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    // Call onSuccess if any creators were processed (added, existing, or attempted)
    if (results && (results.added > 0 || results.existing > 0 || results.creators?.length > 0) && onSuccess) {
      onSuccess(results);
    }
    setPasteInput('');
    setCsvFile(null);
    setResults(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Bulk Add Creators</h2>
            <p className="text-sm text-gray-500">Add multiple creators at once</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!results ? (
            <>
              {/* Import name — only shown when tracking as a persistent job */}
              {asImportJob && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Import name <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={importName}
                    onChange={(e) => setImportName(e.target.value)}
                    placeholder="e.g. Helldivers 2 outreach — April"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sc-pink focus:ring-1 focus:ring-sc-pink"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Gives the import a label in your Imports history so you can find it later to download the enriched CSV.
                  </p>
                </div>
              )}

              {/* Input Method Toggle */}
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => setInputMethod('paste')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    inputMethod === 'paste'
                      ? 'bg-sc-pink/20 text-sc-pink'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📝 Paste URLs
                </button>
                <button
                  onClick={() => setInputMethod('csv')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    inputMethod === 'csv'
                      ? 'bg-sc-pink/20 text-sc-pink'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📄 Upload CSV
                </button>
              </div>

              {/* Paste Input */}
              {inputMethod === 'paste' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paste creator URLs (one per line)
                  </label>
                  <textarea
                    value={pasteInput}
                    onChange={(e) => setPasteInput(e.target.value)}
                    placeholder={`https://twitch.tv/shroud
https://kick.com/xqc
https://youtube.com/@MrBeast
https://tiktok.com/@charlidamelio
https://youtube.com/c/pewdiepie`}
                    className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Supported: Twitch, Kick, YouTube, and TikTok URLs. Plain usernames are not accepted.
                  </p>
                </div>
              )}

              {/* CSV Upload */}
              {inputMethod === 'csv' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload CSV file
                  </label>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      <div className="text-4xl mb-2">📄</div>
                      {csvFile ? (
                        <p className="text-gray-900 font-medium">{csvFile.name}</p>
                      ) : (
                        <>
                          <p className="text-gray-600">Click to select a CSV file</p>
                          <p className="text-sm text-gray-400 mt-1">or drag and drop</p>
                        </>
                      )}
                    </label>
                  </div>

                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Expected CSV columns:</p>
                    <code className="text-xs bg-gray-200 px-2 py-1 rounded">
                      name, twitch_url, kick_url, youtube_url, tiktok_url, twitter, email
                    </code>
                    <p className="text-xs text-gray-500 mt-2">
                      Column headers are flexible. Use full URLs for Twitch, Kick, YouTube, and TikTok columns.
                    </p>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}
            </>
          ) : (
            /* Results */
            <div>
              <div className="text-center mb-6">
                <div className="text-5xl mb-4">
                  {results.added > 0 ? '✅' : '⚠️'}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {results.added > 0 ? 'Creators Added!' : 'Processing Complete'}
                </h3>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{results.added}</p>
                  <p className="text-sm text-green-700">Added</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-600">{results.existing || 0}</p>
                  <p className="text-sm text-yellow-700">Already Existed</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{results.failed || 0}</p>
                  <p className="text-sm text-red-700">Failed</p>
                </div>
              </div>

              {/* Added Creators */}
              {results.creators && results.creators.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Added Creators:</p>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                    {results.creators.map((creator, idx) => (
                      <div key={idx} className="px-4 py-2 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                          {creator.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{creator.name}</p>
                          <div className="flex gap-2 text-xs text-gray-500">
                            {creator.twitchHandle && <span>Twitch: {creator.twitchHandle}</span>}
                            {creator.kickHandle && <span>Kick: {creator.kickHandle}</span>}
                            {creator.youtubeHandle && <span>YouTube: {creator.youtubeHandle}</span>}
                            {creator.tiktokHandle && <span>📱 {creator.tiktokHandle}</span>}
                          </div>
                        </div>
                        <span className="text-green-500">✓</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {results.errors && results.errors.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Errors:</p>
                  <div className="max-h-32 overflow-y-auto bg-red-50 border border-red-200 rounded-lg p-3">
                    {results.errors.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-700">
                        {err.input}: {err.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button
              onClick={handleProcess}
              disabled={processing || (inputMethod === 'paste' && !pasteInput.trim()) || (inputMethod === 'csv' && !csvFile)}
              className="px-6 py-2 bg-sc-pink text-white rounded-lg hover:bg-sc-pink/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {processing ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  ➕ Add Creators
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkCreatorUpload;
